import React from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ShoppingBag, Camera, Sparkles, ArrowRight, CalendarDays, Play } from "lucide-react";

const FEATURES = [
  [CalendarCheck, "Your week, sorted in one tap", "Millie plans seven dinners around your family's tastes, budget and busy nights."],
  [ShoppingBag, "A Woolies or Coles list that just works", "Products named the way your supermarket names them — copy, scan or send to the app."],
  [Camera, "Cook with what you've got", "Snap your fridge and pantry and Millie plans around it before you buy a thing."],
  [Sparkles, "Little Wins for fussy eaters", "Track every new food tried, and get a warm Sunday recap in your inbox."],
  [CalendarDays, "Busy nights, sorted", "Connect your Google Calendar (read-only) and Millie spots evenings with something on, so she plans quick dinners on those nights. Sorted only reads events. It never changes your calendar, and you can disconnect any time."],
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FFF6F1] text-sorted-ink" data-testid="landing-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="Sorted" width={44} height={44} className="rounded-2xl shadow-soft" />
          <span className="font-hand text-3xl text-sorted-rose">Sorted</span>
        </div>
        <nav className="flex items-center gap-2 text-sm font-bold">
          <Link to="/login" data-testid="landing-login" className="px-4 py-2 rounded-full bg-white border-2 border-[#FDECEF]">Sign in</Link>
          <Link to="/register" data-testid="landing-register" className="px-4 py-2 rounded-full bg-sorted-rose text-white">Get started</Link>
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-6">
        <section className="mt-12 md:mt-20 grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div>
            <p className="text-xs font-bold text-sorted-rose tracking-wide uppercase">Meet Millie, your household's meal planner</p>
            <h1 className="mt-3 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Your household. <span className="text-sorted-rose">Sorted.</span></h1>
            <p className="mt-5 text-base md:text-lg text-sorted-slate font-semibold max-w-md">Dinners planned, the shopping list written in Woolworths or Coles language, and a warm Aussie voice that remembers what your family actually eats.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" data-testid="landing-cta" className="px-6 py-3.5 rounded-full bg-sorted-ink text-white font-bold flex items-center gap-2 shadow-soft">Start free <ArrowRight size={16} strokeWidth={2.5} /></Link>
              <Link to="/demo" data-testid="landing-demo" className="px-6 py-3.5 rounded-full bg-white border-2 border-[#FDECEF] font-bold flex items-center gap-2"><Play size={16} strokeWidth={2.5} className="text-sorted-rose" /> Try the demo</Link>
              <Link to="/plans" className="px-6 py-3.5 rounded-full bg-white border-2 border-[#FDECEF] font-bold">See plans</Link>
            </div>
          </div>
          <img src="/icon-1024.png" alt="Millie" className="w-56 md:w-72 mx-auto rounded-[28%] shadow-soft rotate-2" />
        </section>

        <section className="mt-16 grid sm:grid-cols-2 gap-4" data-testid="landing-features">
          {FEATURES.map(([Icon, title, body], i) => (
            <div key={title} className={`bg-white rounded-3xl border-2 border-[#FDECEF] p-5 shadow-soft ${i === FEATURES.length - 1 ? "sm:col-span-2" : ""}`} data-testid={`landing-feature-${i + 1}`}>
              <span className="w-11 h-11 rounded-2xl bg-sorted-peach/60 flex items-center justify-center"><Icon size={20} strokeWidth={2.5} /></span>
              <h2 className="mt-3 font-heading font-semibold text-base md:text-lg leading-tight">{title}</h2>
              <p className="mt-1 text-sm text-sorted-slate font-semibold">{body}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="max-w-3xl mx-auto px-6 mt-16 pb-10 border-t border-[#FDECEF] pt-6 text-xs font-bold text-sorted-slate" data-testid="landing-footer">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Sorted by Millie · Made in Australia</p>
          <nav className="flex items-center gap-2" data-testid="landing-legal-links">
            <Link to="/privacy" data-testid="landing-privacy" className="underline underline-offset-2">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" data-testid="landing-terms" className="underline underline-offset-2">Terms of Service</Link>
            <span>|</span>
            <a href="mailto:hello@sortedbymillie.com.au" data-testid="landing-contact" className="underline underline-offset-2">Contact</a>
          </nav>
        </div>
        <p className="mt-4 text-center sm:text-left font-semibold leading-relaxed" data-testid="landing-google-disclosure">
          Sorted's use and transfer of information received from Google APIs adheres to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" className="underline underline-offset-2">Google API Services User Data Policy</a>, including the Limited Use requirements.
        </p>
      </footer>
    </div>
  );
}
